/**
 * AdminScoringRunsPage — handoff §6.1.5.
 *
 * Table of recent scoring runs. The backend response is already grouped
 * by `run_id` (each row is a unique run). Filterable by performance year
 * via a top input.
 */
import { useMemo, useState } from "react";
import { Spinner } from "@tensaw/design-system";
import { SchemaDataGrid, type SchemaDataGridColumn } from "@tensaw/composition";
import { useScoringRuns } from "@/hooks/useScoringRuns";
import { formatDate, formatDateTime } from "@/lib/format";
import type { ScoringRunSummary } from "@/api/models";

export function AdminScoringRunsPage() {
  const [yearFilter, setYearFilter] = useState<string>("");
  const yearNum = yearFilter.trim() === "" ? undefined : Number(yearFilter);
  const runsQ = useScoringRuns(yearNum);

  const columns = useMemo<SchemaDataGridColumn<ScoringRunSummary>[]>(
    () => [
      {
        id: "snapshot_date",
        header: "Snapshot date",
        width: 140,
        cell: ({ row }) => <span className="tnum text-sm">{formatDate(row.snapshot_date)}</span>,
      },
      {
        id: "run_id",
        header: "Run ID",
        minWidth: 240,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-gray-700">{row.run_id}</span>
        ),
      },
      {
        id: "year",
        header: "Year",
        width: 80,
        align: "right",
        cell: ({ row }) => <span className="num-cell text-sm">{row.performance_year}</span>,
      },
      {
        id: "clinics",
        header: "Clinics",
        width: 90,
        align: "right",
        cell: ({ row }) => <span className="num-cell text-sm">{row.clinics_count}</span>,
      },
      {
        id: "measures",
        header: "Measures",
        width: 100,
        align: "right",
        cell: ({ row }) => <span className="num-cell text-sm">{row.measures_count}</span>,
      },
      {
        id: "earliest",
        header: "Started",
        width: 180,
        cell: ({ row }) => <span className="tnum text-xs text-gray-600">{formatDateTime(row.earliest_computed_at)}</span>,
      },
      {
        id: "latest",
        header: "Finished",
        width: 180,
        cell: ({ row }) => <span className="tnum text-xs text-gray-600">{formatDateTime(row.latest_computed_at)}</span>,
      },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-tw-teal-400">
          Admin
        </div>
        <h1 className="text-2xl font-semibold text-gray-800">Scoring runs</h1>
        <p className="mt-1 text-sm text-gray-600">
          Audit log of every scoring computation. Filterable by performance
          year.
        </p>
      </div>

      <div className="flex items-center gap-3 rounded-lg border border-border bg-white px-4 py-3">
        <label htmlFor="run-year" className="text-xs font-medium uppercase tracking-wider text-gray-500">
          Filter by year
        </label>
        <input
          id="run-year"
          type="number"
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value)}
          placeholder="(all)"
          className="w-28 rounded border border-border px-2 py-1 text-sm tnum"
        />
        {yearFilter ? (
          <button
            type="button"
            onClick={() => setYearFilter("")}
            className="text-xs text-tw-teal-700 hover:underline"
          >
            Clear
          </button>
        ) : null}
      </div>

      {runsQ.isLoading ? (
        <div className="flex h-32 items-center justify-center" role="status">
          <Spinner />
        </div>
      ) : runsQ.isError || !runsQ.data ? (
        <div className="rounded-lg border border-border bg-white p-6 text-sm text-gray-600">
          Couldn't load scoring runs.{" "}
          {runsQ.error instanceof Error ? runsQ.error.message : ""}
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-white">
          <SchemaDataGrid<ScoringRunSummary>
            rows={runsQ.data.runs}
            columns={columns}
            getRowId={(r) => r.run_id}
            density="compact"
            emptyTitle="No scoring runs"
            emptyBody="No runs match the current filter."
          />
        </div>
      )}
    </div>
  );
}
